


























































import { Component, Mixins, Watch } from 'vue-property-decorator'
import Axios from 'axios'
import { timeout } from '@/helpers/generic'
import { ICommonLocation } from '@/helpers/features/common-locations'
import { LibraryMixin } from '@/mixins'
import { spawn, Thread, Worker } from 'threads'
import { waitForMinimumDelay } from '@/helpers/date'
import { numberFilter } from '@/filters'
import { escapeRegex } from '@/helpers/regex'
import { ITutorialSlide } from '@/components/Tutorial.vue'

@Component({
  components: {
    ProgressLoader: require('@/components/ProgressLoader').default,
    Tutorial: require('@/components/Tutorial').default
  }
})
export default class Relocate extends Mixins(LibraryMixin) {
  loadingLocations: boolean = false
  loading: boolean = false
  locations: ICommonLocation[] = []

  sourcePath: string = ''
  targetPath: string = ''

  tutorialSlides: ITutorialSlide[] = [
    {
      html: `With <i>Relocate Files</i> you can quickly change the locations of your tracks. This is especially helpful if you are moving your tracks to a new drive or if you're changing your music folder structure.<br><br>In this tutorial I'm going to show you how to quickly change locations of lots of tracks at the same time.`
    },
    {
      html: `About half of my tracks are on the <b>Z:/</b> drive, but I want to change that to <b>D:/</b>.<br>All I have to do is enter <b>Z:/</b> in the <i>Existing location</i> field and <b>D:/</b> in the <i>New location</i> field. Rekordcloud immediately tells me what all the new locations are going to be in the column on the right. After confirming that is correct, I press Save and it's already done.<br><br>The locations for these tracks are updated and the next time I download my library, the tracks will be found at the new location.`,
      image: 'relocatefiles1.png'
    },
    {
      html: `But now I also want to change my music folder structure a bit. I want to rename the folder <b>C:/Users/Christiaan/Music</b> to <b>DJ Music</b>.<br>I can click the locations in the table so I don't need to type it myself. On the right, I'm entering  <b>C:/Users/Christiaan/DJ Music/</b> so only the <b>Music</b> folder is going to change.<br>Now I just need to save and rekordcloud is going to update the locations. After saving, you'll see the changes in rekordcloud immediately.<br><br>That's the end of the tutorial. With <i>Relocate Files</i> you can update your track locations in bulk, just be careful that the new location is exactly right or your DJ app won't find your track.`,
      image: 'relocatefiles2.png'
    }
  ]

  setSourcePath(path: string) {
    if (this.loading) return

    this.sourcePath = path
  }

  setTargetPath(path: string) {
    if (this.loading) return

    this.targetPath = path
  }

  getTargetPath(oldPath: string) {
    if (!this.targetPath) return oldPath

    return oldPath.replace(new RegExp(escapeRegex(this.sourcePath), 'i'), this.targetPath)
  }

  async fetchLocations() {
    if (!this.library) return

    this.loadingLocations = true

    const startTime = new Date()

    const worker = await spawn(new Worker('@/helpers/features/common-locations'))

    this.locations = await worker.getCommonLocations(this.library.tracks)

    await Thread.terminate(worker)

    await waitForMinimumDelay(startTime, 500)

    this.loadingLocations = false
  }

  async save() {
    if (!this.canSave) return

    if (this.sourcePath.endsWith('/') && !this.targetPath.endsWith('/')) {
      const { result } = await this.$buefy.dialog.confirm({
        type: 'is-warning',
        title: 'Are you sure?',
        message: 'Your existing location ends with a / (slash) but your new location does not. Are you sure this is correct? If you are missing a / (slash) then your new folder location will not be correct.<br><br>Please check the results in the right side of the table.'
      })

      if (!result) return
    }

    this.loading = true

    try {
      const { data } = await Axios.post('/library/change-location', {
        sourcePath: this.sourcePath,
        targetPath: this.targetPath
      })

      this.$buefy.notification.open(`Changed locations for ${numberFilter(data)} tracks`)

      await this.$store.dispatch('library/fetchLibrary')
    } catch (error) {
      console.error(error)

      if (this.$store.state.auth.isDemoUser) {
        this.$buefy.notification.open(`Changed track locations`)
      }
    }

    this.locations = []

    this.loading = false
    this.targetPath = ''
  }

  async created() {
    await this.fetchLocations()
  }

  get containsBackslash(): boolean {
    return this.targetPath.includes('\\')
  }

  get canSave(): boolean {
    return !!this.sourcePath && !!this.targetPath && this.sourcePath !== this.targetPath && !this.containsBackslash && this.matchingTrackAmount > 0
  }

  get matchingTrackAmount(): number {
    if (!this.sourcePath) return 0

    const loc = this.locations.find(l => l.path.toLowerCase().includes(this.sourcePath.toLowerCase()))

    if (loc) return loc.count
    else return 0
  }

  @Watch('$store.state.library.library')
  async watchLibrary() {
    await timeout(10)

    await this.fetchLocations()
  }
}
